import { ApolloProvider } from "@apollo/client";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import * as Sentry from "@sentry/react";
import "pure-react-carousel/dist/react-carousel.es.css";
import React, { useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes as RouterRoutes } from "react-router-dom";
import { msalConfig } from "./authConfig";
import { CommandPaletteProvider } from "./components/ui/CommandPalette/CommandPaletteProvider";
import { getAppEnvironment, getPublicConfig } from "./config";
import { applyEnvironmentFavicon } from "./favicon";
import getClient from "./graphql/client";
import useMSAL from "./hooks/useMSAL";
import reportWebVitals from "./reportWebVitals";
import Routes from "./routes";

applyEnvironmentFavicon();

Sentry.init({
  dsn: "https://43d03ab259f3bd4faf64b6cad7981267@o4509530751500288.ingest.de.sentry.io/4509644155715664",
  environment: getAppEnvironment(),
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Disable built-in masking
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.2, //  Capture 20% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/api\.new\.inventory\.whitecube\.com/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.05, // This sets the sample rate at 5%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// ActiveReports is now loaded dynamically when needed
// License key is set in util/activeReportsInit.js

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();
const App = () => {
  useEffect(() => {
    window.debug = { env: getPublicConfig() };
  }, []);

  const { isAuthenticated, requestAccessToken, token } = useMSAL();
  const client = getClient({ requestAccessToken });

  const routesProps = {
    isAuthenticated,
    token,
  };

  const memoizedRoutes = useMemo(() => {
    return (
      <ApolloProvider client={client}>
        <CommandPaletteProvider>
          <Routes {...routesProps} />
        </CommandPaletteProvider>
      </ApolloProvider>
    );
  }, [isAuthenticated, token]);

  return <>{memoizedRoutes}</>;
};

const MainRoutes = () => {
  const routes = [
    {
      path: "*",
      element: (
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      ),
    },
    {
      path: "/auth",
      element: <div></div>,
    },
  ];

  const renderRoutes = routes?.map((item, index) => {
    return <Route key={index} {...item} />;
  });

  return (
    <BrowserRouter>
      <RouterRoutes>{renderRoutes}</RouterRoutes>
    </BrowserRouter>
  );
};

const container = document.getElementById("root");
const root = createRoot(container);
// eslint-disable-next-line
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div>An error occurred</div>}>
      <MainRoutes />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
